import { z } from 'zod';
import { useMemo } from 'react';
import { format, parse } from 'date-fns';
import { orderBy } from 'lodash-es';
import { ContractorFormType, NameByValue } from '@/types';
import { Location } from '@/api/types';
import { OrganType } from '@/api/apis/case';
import { AffiliationType, Employee, EmployeeRole, LocationPreference, ScrubSize } from '@/api/apis/resources';
import { Hub } from '@/api/apis/facilities';
import { STATE_SCHEMA, getNameValuesFromEnumNames } from '@/utils/helpers';
import { EMPLOYEE_ROLE_NAMES } from '@/constants/user.constants';
import { TMDXFile } from '@/api/apis/files';
import { useCountriesData } from '@/contexts/CountriesDataProvider';
import { createContractorFormValidation } from './utils';

export const EMPLOYEE_ROLES_SELECT_LIST_CONTRACTOR = orderBy(getNameValuesFromEnumNames(EMPLOYEE_ROLE_NAMES), 'name');

export const getDefaultContractorValues = (
  data: Employee,
  homeLocation?: Location,
  baseLocation?: Hub | null,
): ContractorFormType => {
  return {
    firstName: data.firstName,
    lastName: data.lastName,
    position: data.position || '',
    roles: data.roles.filter((r) => Object.values(EmployeeRole).includes(r)),
    email: data.email,
    phone: data.phone || '',
    hireDate: data.hireDate ? format(parse(data.hireDate, 'yyyy-MM-dd', new Date()), 'MM / dd / yyyy') : '',
    dateOfBirth: data.dateOfBirth ? format(parse(data.dateOfBirth, 'yyyy-MM-dd', new Date()), "MMM do',' yyyy") : '',
    weight: data.weight,
    scrubSizeTop: data.scrubSizeTop,
    scrubSizeBottom: data.scrubSizeBottom,
    tsaPrecheckNumber: data.tsaPrecheckNumber || null,
    organType: data.organSpecializations.filter((r) => Object.values(OrganType).includes(r)),
    isActive: data.isActive,
    locationPreference: data.locationPreference,
    profilePhoto: data.profilePhoto ? data.profilePhoto : '',
    addressId: homeLocation?.id || '',
    addressLine: homeLocation?.addressLine || '',
    city: homeLocation?.city || '',
    state: homeLocation?.state || null,
    postalCode: homeLocation?.postalCode || '',
    country: homeLocation?.country || '',
    currentLocation: data.currentLocation,
    baseLocationId: baseLocation?.id || null,
    longitude: homeLocation?.longitude ?? 0,
    latitude: homeLocation?.latitude ?? 0,
    closestAirportForDeploymentId: data?.closestAirportForDeployment?.toString(),
    affiliationType: data?.affiliation?.affiliationType ?? AffiliationType.TMDXContract,
    affiliationTxCenterId: data.affiliation?.txCenterId || null,
    affiliationOpoId: data.affiliation?.opoId || null,
    contractorCertifications: data.contractorCertifications
      ? data.contractorCertifications.map((c) => ({
          ...c,
          certificationDate: format(new Date(c.certificationDate), 'yyyy-MM-dd'),
        }))
      : null,
    shadowOrganSpecializations: data.shadowOrganSpecializations,
    proctorOrganSpecializations: data.proctorOrganSpecializations,
    homeLocationTimezone: homeLocation?.timezone || '',
  };
};

export const contractorCertificationSchema = z.object({
  organType: z.enum(OrganType),
  certificationDate: z.string({ error: 'Select certification date' }),
});

export const contractorFormSchema = (composedSubdivisionsList: Record<string, NameByValue[]>) =>
  z
    .object({
      profilePhoto: z.string().optional(),
      image: z.custom<TMDXFile>().optional(),
      firstName: z.string(),
      lastName: z.string(),
      email: z.string(),
      phone: z.string(),
      hireDate: z.string({ error: 'Select hire date' }),
      dateOfBirth: z.string(),
      tsaPrecheckNumber: z.string().nullable(),
      position: z.string().optional(),
      roles: z.enum(EmployeeRole, { error: 'Invalid Role' }).array().nonempty('Provide role'),
      organType: z.array(z.enum(OrganType)),
      isActive: z.boolean(),
      weight: z.number().positive('Provide weight').nullable(),
      scrubSizeTop: z.enum(ScrubSize).nullable(),
      scrubSizeBottom: z.enum(ScrubSize).nullable(),
      currentLocation: z.custom<Location>(),
      baseLocationId: z.string().min(1).nullable(),
      closestAirportForDeploymentId: z.string().optional(),
      locationPreference: z.enum(LocationPreference, { error: 'Select location' }),
      addressId: z.string().min(1, 'Provide address'),
      addressLine: z.string().min(1, 'Provide address'),
      city: z.string().min(1, 'Provide city'),
      state: STATE_SCHEMA,
      postalCode: z.string().min(1, 'Provide postal code'),
      country: z.string(),
      latitude: z.number().min(-90).max(90),
      longitude: z.number().min(-180).max(180),
      affiliationType: z.enum(AffiliationType, { error: 'Select Affiliation Type' }),
      affiliationTxCenterId: z.string().nullable(),
      affiliationOpoId: z.string().nullable(),
      contractorCertifications: z.array(contractorCertificationSchema),
      shadowOrganSpecializations: z.array(z.enum(OrganType)),
      proctorOrganSpecializations: z.array(z.enum(OrganType)),
      homeLocationTimezone: z.string(),
    })
    .superRefine(createContractorFormValidation(composedSubdivisionsList));

export const useContractorFormSchema = () => {
  const { composedSubdivisionsList, isLoading } = useCountriesData();
  const schema = useMemo(() => contractorFormSchema(composedSubdivisionsList), [composedSubdivisionsList]);
  return { schema, isLoading };
};
